import React from 'react';

import {Box, Icon, Typography} from '@mui/material';
import './styles';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {
  AppButton,
  AppButtonVariant,
  ButtonContainer,
  Note,
  NoteType,
} from '@/app/shared-components';
import ProgressBar from '@/app/shared-components/ProgressBar/ProgressBar';
import {LINKS} from '@/constants/LinksConstants';
import {COLORS} from '@/styles/colors';

import {defaultAcceptedFiles, useActions} from './useActions';

const MAX_FILE_SIZE = 10;
const UPLOAD_PROGRESS_DURATION = 500;

type KycDocumentUploaderProps = {
  acceptFiles?: string;
  sizeMb?: number;
  onSubmit: () => void;
};

export const KycDocumentUploader: React.FC<KycDocumentUploaderProps> = ({
  acceptFiles = defaultAcceptedFiles,
  sizeMb = MAX_FILE_SIZE,
  onSubmit,
}) => {
  const {t} = useTranslation();

  const {
    fileInputRef,
    dropZoneRef,
    isFirstStep,
    isLastStep,
    canUploadFile,
    isUploadingFile,
    isFileUploaded,
    isDisabledButton,
    currentFileName,
    onBack,
    triggerFileInput,
    onFileSelected,
    onDragLeave,
    onDragOver,
    onDrop,
    cancelUpload,
    removeFile,
    onContinue,
    onSubmitForm,
  } = useActions({
    acceptFiles,
    sizeMb,
    onSubmit,
  });

  return (
    <Box className="file-uploader-container">
      {!isFirstStep && isLastStep && (
        <Box
          className="back-button"
          onClick={onBack}>
          <Icon style={{fontSize: 20, color: '#646867'}}>arrow_back</Icon>
        </Box>
      )}

      <Box
        ref={dropZoneRef}
        className="upload-box-container"
        onClick={triggerFileInput}
        sx={{
          cursor: canUploadFile ? 'pointer' : 'default',
        }}>
        {canUploadFile && (
          <Box
            className="upload"
            onDragOver={onDragOver}
            onDrop={onDrop}
            onDragLeave={onDragLeave}>
            <img
              src="/assets/icons/file_upload.svg"
              alt={t('kyc.idv_docs.uploader.upload_icon_alt')}
              width="32"
              height="32"
            />
            <Box className="text-container">
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptFiles}
                id="fileInput"
                onChange={onFileSelected}
                hidden
              />
              <Typography
                component={'p'}
                className="title-step">
                <Typography
                  component={'span'}
                  className="choose-file">
                  {t('kyc.idv_docs.uploader.choose_file')}
                </Typography>
              </Typography>
              <Box className="limit-container">
                <Typography className="note">
                  {t('kyc.idv_docs.uploader.size_limit', {sizeMb})}
                </Typography>
                <Typography className="note">
                  {t('kyc.idv_docs.uploader.supported_file_types')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {isUploadingFile && (
          <Box className="uploading-container">
            <Box className="text-container">
              <Typography className="text-step">{currentFileName}</Typography>
              <Icon
                sx={{cursor: 'pointer'}}
                style={{fontSize: 24, color: '#000000'}}
                onClick={cancelUpload}>
                close
              </Icon>
            </Box>

            <Box className="progress-bar">
              <ProgressBar
                duration={UPLOAD_PROGRESS_DURATION}
                value={0}
                waitForReady
                isReady={!isUploadingFile}
              />
            </Box>
          </Box>
        )}

        {isFileUploaded && !isUploadingFile && (
          <Box className="uploaded">
            <Box className="text-container">
              <Typography
                component={'span'}
                className="title-step">
                {t('kyc.idv_docs.uploader.file_uploaded_successfully')}
              </Typography>
              <img
                src="/assets/images/pages/documentUploaded.webp"
                alt={t('kyc.idv_docs.uploader.upload_success_alt')}
                width="32"
                height="32"
              />
              <Typography className="file">{currentFileName}</Typography>
            </Box>
            <AppButton
              id="kyc-document-uploader-remove-file-button"
              label={t('kyc.idv_docs.uploader.remove_file_button')}
              variant={AppButtonVariant.onText}
              labelColor={COLORS.ON_SURFACE_1}
              onClick={removeFile}
            />
          </Box>
        )}
      </Box>

      <Note
        type={NoteType.ALERT}
        text={t('kyc.idv_docs.uploader.accepted_documents_note')}
      />

      <Typography className="privacy-policy">
        {t('kyc.idv_docs.uploader.privacy_policy_prefix')}
        <Link
          className="link"
          to={LINKS.PRIVACY_POLICY}
          target="_blank"
          rel="noopener noreferrer">
          {t('kyc.idv_docs.uploader.privacy_policy_link')}
        </Link>
        {t('kyc.idv_docs.uploader.privacy_policy_suffix')}
      </Typography>

      <ButtonContainer sticky={true}>
        {!isLastStep && (
          <AppButton
            id="kyc-document-uploader-continue-button"
            label={t('kyc.idv_docs.uploader.continue_button')}
            variant={AppButtonVariant.onPrimary}
            onClick={onContinue}
            disabled={isDisabledButton}
          />
        )}

        {isLastStep && (
          <AppButton
            id="kyc-document-uploader-submit-button"
            label={t('kyc.idv_docs.uploader.submit_button')}
            variant={AppButtonVariant.onPrimary}
            onClick={onSubmitForm}
            disabled={isDisabledButton}
          />
        )}
      </ButtonContainer>
    </Box>
  );
};
